import { invalidateAllQueries } from "bonzai"
import { useGraphqlQuery } from "graphql-mock"
import { toast } from "materia"
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react"
import { ConfettiBanner } from "."
import { query } from "./gql"
import { triggerConfetti } from "./triggerConfetti"

const CONFETTI_TRIGGER_EVENT_KEY = "confetti-trigger"

type ProductStatusUpdate = {
  productId: string
  type: "high_score" | "certified"
  value?: number
}

export type ConfettiContextType = {
  isConfettiBlocked: boolean
  setIsConfettiBlocked: (isBlocked: boolean) => void
  productStatusUpdate: ProductStatusUpdate | undefined
}

export const ConfettiContextProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [isConfettiBlocked, setIsConfettiBlocked] = useState(false)
  const [productStatusUpdate, setProductStatusUpdate] = useState<
    ProductStatusUpdate | undefined
  >()

  const { productId, type, value: progressValue } = productStatusUpdate || {}

  const { data, error, isPending } = useGraphqlQuery(
    query,
    { id: productId! },
    { enabled: Boolean(productId) }
  )

  const { title = "" } = data?.product || {}
  const shouldShowBanner =
    !isConfettiBlocked && Boolean(productStatusUpdate) && !error && !isPending

  const clearProductStatusUpdate = () => {
    setProductStatusUpdate(undefined)
  }

  useEffect(() => {
    if (shouldShowBanner) {
      // [TODO] Should we target specific queries to invalidate?
      invalidateAllQueries()
      triggerConfetti()
      toast(
        <ConfettiBanner
          progressType={type}
          progressValue={progressValue}
          productTitle={title}
        />,
        {
          onDismiss: clearProductStatusUpdate,
          onAutoClose: clearProductStatusUpdate,
        }
      )
    }
  }, [shouldShowBanner, type, progressValue, title])

  useEffect(() => {
    const handleConfettiTrigger = (event: CustomEvent<ProductStatusUpdate>) => {
      setProductStatusUpdate(event.detail)
    }

    document.addEventListener(
      CONFETTI_TRIGGER_EVENT_KEY,
      handleConfettiTrigger as EventListener
    )

    return () => {
      document.removeEventListener(
        CONFETTI_TRIGGER_EVENT_KEY,
        handleConfettiTrigger as EventListener
      )
    }
  }, [])

  return (
    <ConfettiContext.Provider
      value={{
        isConfettiBlocked,
        setIsConfettiBlocked,
        productStatusUpdate,
      }}
    >
      {children}
    </ConfettiContext.Provider>
  )
}

const ConfettiContext = createContext({} as ConfettiContextType)
export const useConfettiContext = (): ConfettiContextType =>
  useContext(ConfettiContext)

/**
 * This hook should be added to every component where
 * we don't want confetti banner appearing immediately,
 * but rather being deferred; ex. ViewerModal
 */
export const useBlockConfettiBanner = () => {
  const { setIsConfettiBlocked } = useConfettiContext()

  useEffect(() => {
    setIsConfettiBlocked(true)

    return () => setIsConfettiBlocked(false)
  }, [setIsConfettiBlocked])
}

export const dispatchConfettiForProgressChange = ({
  productId,
  before,
  after,
}: {
  productId: string
  before: { certified: boolean; highScore: number }
  after: { certified: boolean; highScore: number }
}) => {
  const isNewlyCertified = !before.certified && after.certified
  const isNewHighScore = before.highScore < after.highScore

  if (isNewlyCertified) {
    dispatchConfettiEvent({ productId, type: "certified" })
  } else if (isNewHighScore) {
    dispatchConfettiEvent({
      productId,
      type: "high_score",
      value: after.highScore,
    })
  }
}

export const dispatchConfettiEvent = (detail: ProductStatusUpdate) => {
  const confettiEvent = new CustomEvent<ProductStatusUpdate>(
    CONFETTI_TRIGGER_EVENT_KEY,
    { detail }
  )

  document.dispatchEvent(confettiEvent)
}
